import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, MapPin, Phone, ShoppingBasket, Filter, Star, MessageCircle } from 'lucide-react';
import axios from 'axios';
import ProductReviews from '../components/ProductReviews';
import { PaymentMethodBadge } from '../components/PaymentIcons';
import BannerCarousel from '../components/BannerCarousel';

const Marketplace = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [producersPaymentMethods, setProducersPaymentMethods] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    city: ''
  });
  const { API, user, siteSettings } = useContext(AuthContext);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProducts();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.city) params.append('city', filters.city);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/products?${params.toString()}`);
      const fetchedProducts = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.products) ? response.data.products : []);
      setProducts(fetchedProducts);

      // Fetch payment methods for each unique producer
      const uniqueProducerIds = [...new Set(fetchedProducts.map(p => p.producer_id))];
      const paymentMethodsPromises = uniqueProducerIds.map(async (producerId) => {
        try {
          const pmResponse = await axios.get(`${API}/producers/${producerId}/payment-methods`);
          return { producerId, paymentMethods: pmResponse.data };
        } catch (error) {
          console.error(`Error fetching payment methods for producer ${producerId}:`, error);
          return { 
            producerId, 
            paymentMethods: {
              pix: false,
              debit_card: false,
              credit_card: false,
              credit_card_brands: []
            }
          };
        }
      });

      const paymentMethodsResults = await Promise.all(paymentMethodsPromises);
      const paymentMethodsMap = {};
      paymentMethodsResults.forEach(({ producerId, paymentMethods }) => {
        paymentMethodsMap[producerId] = paymentMethods;
      });
      setProducersPaymentMethods(paymentMethodsMap);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', city: '' });
  };

  const canAccessWhatsApp = () => {
    // Se a configuração exige login, verifica se o usuário está logado
    if (siteSettings?.whatsapp_requires_login) {
      return !!user;
    }
    // Se não exige login, sempre permite acesso
    return true;
  };

  const handleWhatsAppClick = (product) => {
    if (!canAccessWhatsApp()) {
      toast.error('Você precisa estar logado para contatar o produtor');
      navigate('/login');
      return;
    }
    
    openWhatsApp(product.producer_phone, product.name, product.producer_name, product.id);
  };

  const openWhatsApp = async (phone, productName, producerName, productId) => {
    try {
      // Track the click
      await axios.post(`${API}/products/${productId}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
      // Continue with WhatsApp even if tracking fails
    }

    const message = encodeURIComponent(
      `Olá ${producerName}! Vi seu produto "${productName}" na ${siteSettings?.site_name || 'Nossa Feira'} e tenho interesse. Podemos conversar?`
    );
    const cleanPhone = phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/55${cleanPhone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      {/* Hero Section */}
      <div 
        className="min-h-[60vh] flex items-center justify-center text-center"
        style={{
          background: siteSettings?.hero_background_color === 'gradient' 
            ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(251, 191, 36, 0.95) 50%, rgba(251, 146, 60, 0.95) 100%)'
            : siteSettings?.hero_background_color === 'solid'
            ? siteSettings?.site_color_primary || '#22c55e'
            : siteSettings?.hero_background_color === 'image' && siteSettings?.hero_background_image_url
            ? `url(${siteSettings.hero_background_image_url}) center/cover`
            : 'linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(251, 191, 36, 0.95) 50%, rgba(251, 146, 60, 0.95) 100%)',
          color: siteSettings?.hero_text_color || '#ffffff'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            {siteSettings?.hero_title || 'Nossa Feira'}
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90 animate-slide-up">
            {siteSettings?.hero_subtitle || 'Conectando produtores locais a consumidores diretos'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
            <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full">
              <ShoppingBasket className="w-5 h-5" />
              <span>Produtos Frescos</span>
            </div>
            <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full">
              <MapPin className="w-5 h-5" />
              <span>Produtores Locais</span>
            </div>
            <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-full">
              <Phone className="w-5 h-5" />
              <span>Contato Direto</span>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Carousel */}
      <div className="max-w-7xl mx-auto px-4 pt-8">
        <BannerCarousel />
      </div>

      {/* Filters Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 card-feira">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <Filter className="w-5 h-5" />
              <span>Filtros:</span>
            </div>
            
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative flex items-center">
                <Search className="absolute left-3 h-4 w-4 text-gray-500 z-10 pointer-events-none" />
                <Input
                  placeholder="Buscar produtos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 input-feira"
                  data-testid="search-input"
                />
              </div>

              <Select 
                value={filters.category || "all"} 
                onValueChange={(value) => handleFilterChange('category', value === "all" ? "" : value)}
                data-testid="category-filter"
              >
                <SelectTrigger className="input-feira">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative flex items-center">
                <MapPin className="absolute left-3 h-4 w-4 text-gray-500 z-10 pointer-events-none" />
                <Input
                  placeholder="Cidade..."
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  className="pl-10 input-feira"
                  data-testid="city-filter"
                />
              </div>
            </div>

            <Button 
              onClick={clearFilters}
              variant="outline" 
              className="whitespace-nowrap"
              data-testid="clear-filters-button"
            >
              Limpar Filtros
            </Button>
          </div>
        </div>

        {/* Products Grid */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Produtos Disponíveis</h2>
          <p className="text-gray-600">
            {loading ? 'Carregando...' : `${products.length} produtos encontrados`}
          </p>
        </div>

        {loading ? (
          <div className="products-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card-feira p-4">
                <div className="w-full h-48 bg-gray-200 rounded-lg mb-4 skeleton"></div>
                <div className="h-4 bg-gray-200 rounded mb-2 skeleton"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 skeleton"></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16" data-testid="no-products-message">
            <ShoppingBasket className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhum produto encontrado</h3>
            <p className="text-gray-500">Tente ajustar os filtros ou verifique novamente mais tarde.</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map((product) => (
              <Card key={product.id} className="card-feira overflow-hidden" data-testid={`product-card-${product.id}`}>
                <div className="aspect-video relative overflow-hidden">
                  {product.image_url ? (
                    <>
                      <img
                        src={`${product.image_url}`}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-green-100 to-orange-100 flex items-center justify-center" style={{display: 'none'}}>
                        <ShoppingBasket className="w-12 h-12 text-green-500" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-green-100 to-orange-100 flex items-center justify-center">
                      <ShoppingBasket className="w-12 h-12 text-green-500" />
                    </div>
                  )}
                  <Badge className="absolute top-2 left-2 category-badge">
                    {product.category}
                  </Badge>
                </div>

                <CardContent className="p-4">
                  <CardTitle className="text-lg font-bold text-gray-800 mb-2">
                    <Link 
                      to={`/product/${product.id}`}
                      className="hover:text-green-600 transition-colors cursor-pointer"
                    >
                      {product.name}
                    </Link>
                  </CardTitle>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-green-600">
                        R$ {product.price.toFixed(2)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {product.quantity} disponível
                      </span>
                    </div>
                    
                    {product.description && (
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {product.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      <span>{product.location}, {product.producer_city}</span>
                    </div>
                    
                    {/* Delivery Options */}
                    {product.delivery_options && (
                      <div className="flex gap-1 text-xs">
                        {product.delivery_options.pickup && (
                          <Badge variant="outline" className="text-xs border-green-200 text-green-700">
                            📍 Retirada
                          </Badge>
                        )}
                        {product.delivery_options.delivery && (
                          <Badge variant="outline" className="text-xs border-blue-200 text-blue-700">
                            🚚 Entrega
                            {product.delivery_options.delivery_fee && 
                              ` R$${product.delivery_options.delivery_fee.toFixed(2)}`}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-gray-800">Produtor:</p>
                        <p className="text-sm text-gray-600">{product.producer_name}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-500">
                          {product.average_rating ? product.average_rating.toFixed(1) : '0.0'}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({product.total_reviews || 0})
                        </span>
                      </div>
                    </div>

                    {/* Payment Methods Section */}
                    {producersPaymentMethods[product.producer_id] && (
                      <div className="mb-3 p-2 bg-blue-50 rounded-lg">
                        <h5 className="text-xs font-medium text-blue-800 mb-1">
                          💳 Aceita:
                        </h5>
                        <div className="flex flex-wrap gap-1">
                          {producersPaymentMethods[product.producer_id].pix && (
                            <PaymentMethodBadge method="pix" className="text-xs" />
                          )}
                          {producersPaymentMethods[product.producer_id].debit_card && (
                            <PaymentMethodBadge method="debit_card" className="text-xs" />
                          )}
                          {producersPaymentMethods[product.producer_id].credit_card && 
                           producersPaymentMethods[product.producer_id].credit_card_brands.length > 0 ? (
                            producersPaymentMethods[product.producer_id].credit_card_brands.slice(0, 2).map(brand => (
                              <PaymentMethodBadge 
                                key={brand} 
                                method="credit_card" 
                                brand={brand}
                                className="text-xs" 
                              />
                            ))
                          ) : producersPaymentMethods[product.producer_id].credit_card && (
                            <PaymentMethodBadge method="credit_card" className="text-xs" />
                          )}
                          {producersPaymentMethods[product.producer_id].credit_card_brands.length > 2 && (
                            <Badge variant="outline" className="text-gray-600 text-xs">
                              +{producersPaymentMethods[product.producer_id].credit_card_brands.length - 2}
                            </Badge>
                          )}
                          {!producersPaymentMethods[product.producer_id].pix && 
                           !producersPaymentMethods[product.producer_id].debit_card && 
                           !producersPaymentMethods[product.producer_id].credit_card && (
                            <Badge variant="outline" className="text-gray-600 text-xs">
                              A combinar
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => handleWhatsAppClick(product)}
                      className={siteSettings?.whatsapp_requires_login && !user 
                        ? "w-full bg-yellow-500 hover:bg-yellow-600 text-white text-sm" 
                        : "w-full whatsapp-btn"
                      }
                      data-testid={`whatsapp-button-${product.id}`}
                    >
                      {siteSettings?.whatsapp_requires_login && !user ? (
                        <>
                          <span className="mr-1">🔒</span>
                          Login para Contatar
                        </>
                      ) : (
                        <>
                          <Phone className="w-4 h-4" />
                          Falar com Produtor
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;